import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatCurriculumPosition } from '../../../../../../../utils/curriculumPosition';

export type PositionSection = {
	id?: number;
	order?: number | null;
	courses_count?: number;
	classroomTab?: { order?: number | null } | null;
};

type Props = {
	control: any;
	watch: (name: any) => any;
	setValue: (name: any, value: any, options?: any) => void;
	sections: PositionSection[];
	/** the saved section / position when editing (undefined when adding) */
	savedSectionId?: number | null;
	savedOrder?: number | null;
	disabled?: boolean;
	sectionField?: string;
	orderField?: string;
};

/**
 * The "Order" input of a course / lecture form: its position inside the
 * selected section, a whole number from 1. Greyed until a section is picked;
 * picking one fills in the section's next position (e.g. 5 when it holds 4
 * courses), which the user can change — the server shifts the others and
 * clamps to the section's range. Going back to the saved section restores the
 * saved position. Shared by the admin and instructor course forms and the
 * standalone lecture form.
 */
const CoursePositionField: React.FC<Props> = ({
	control,
	watch,
	setValue,
	sections,
	savedSectionId,
	savedOrder,
	disabled,
	sectionField = 'section_id',
	orderField = 'order',
}) => {
	const sectionId = watch(sectionField);
	const position = watch(orderField);
	const section = sections.find((item) => Number(item.id) === Number(sectionId));
	const isSavedSection = !!savedSectionId && Number(savedSectionId) === Number(sectionId);
	const count = Number(section?.courses_count ?? 0);
	// in its own section the course is one of the `count`; elsewhere it's a newcomer
	const last = isSavedSection ? Math.max(count, 1) : count + 1;

	const previousSection = useRef<unknown>(undefined);
	useEffect(() => {
		// wait for the section list; skip the first fill of an edit form
		if (!sectionId || !section) return;
		const first = previousSection.current === undefined;
		previousSection.current = sectionId;
		if (isSavedSection) {
			if (!first && savedOrder) setValue(orderField, savedOrder, { shouldDirty: true, shouldValidate: true });
			return;
		}
		setValue(orderField, last, { shouldDirty: !first || !savedSectionId, shouldValidate: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sectionId, section?.id]);

	return (
		<PositionWrapper>
			<EdFormControl
				control={control}
				width="4.75rem"
				type="number"
				inputMode="numeric"
				name={orderField}
				label={'Order'}
				disabled={disabled || !sectionId}
				inputProps={{ min: 1, max: last, step: 1 }}
			/>
			<PositionHint>
				{sectionId && section
					? `${formatCurriculumPosition(section.classroomTab?.order, section.order, position)} · positions 1–${last}; the other courses shift`
					: 'Pick a section first'}
			</PositionHint>
		</PositionWrapper>
	);
};

export default CoursePositionField;

const PositionWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const PositionHint = styled.span`
	font-size: 0.75rem;
	color: #6e7577;
	margin-top: 0.25rem;
`;
